import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { buildEventPath } from "../../utils/eventUrl";
import { PRESS_HOVER, PRESS_TAP, PRESS_TRANSITION } from "../../utils/motionInteractions";
import { LandingHeaderHeroFrame } from "./sections/components/LandingHeader";
import landingHeaderStyles from "./css/LandingHeader.module.css";
import "./css/BratislavaEventsLanding.css";

const PAGE_PATH = "/podujatia-bratislava/";
const PAGE_URL = `https://vaultgrid.net${PAGE_PATH}`;
const EVENT_LIMIT = 6;

const formatEventDate = (value) => {
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("sk-SK", {
      weekday: "short",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Bratislava",
    }).format(date);
  } catch {
    return "";
  }
};

const isPublicBratislavaEvent = (event) => {
  const status = String(event?.status || "").toLowerCase();
  return Boolean(
    event?.id &&
      event?.is_validated &&
      event?.type === "event" &&
      ["active", "postponed", "pending"].includes(status) &&
      String(event?.city || "").trim().toLowerCase() === "bratislava"
  );
};

const getEventCoverSrc = (event) =>
  String(
    event?.hero_image_url ||
      event?.cover_image_url ||
      event?.cover_url ||
      event?.event?.hero_image_url ||
      ""
  ).trim() || null;

const EventCard = ({ event, onOpen }) => {
  const seats = Number(event?.available_slots);
  const capacity = Number(event?.max_participants);
  const hasSeatInfo = Number.isFinite(seats) && Number.isFinite(capacity) && capacity > 0;
  const isSoldOut = hasSeatInfo && seats <= 0;
  const coverSrc = getEventCoverSrc(event);

  return (
    <motion.button
      type="button"
      className="vg-bratislava-event-card"
      onClick={() => onOpen(event)}
      whileHover={PRESS_HOVER}
      whileTap={PRESS_TAP}
      transition={PRESS_TRANSITION}
    >
      {coverSrc ? (
        <img
          className="vg-bratislava-event-card__image"
          src={coverSrc}
          alt=""
          loading="lazy"
          onError={(imageEvent) => {
            imageEvent.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <span className="vg-bratislava-event-card__topline">
        <span>{event?.category || "Podujatie"}</span>
        <span>{isSoldOut ? "Obsadené" : "Bratislava"}</span>
      </span>
      <strong>{event?.name || "Podujatie vo VaultGrid"}</strong>
      <span className="vg-bratislava-event-card__date">{formatEventDate(event?.date_time)}</span>
      {hasSeatInfo ? (
        <span className={`vg-bratislava-event-card__availability${isSoldOut ? " is-full" : ""}`}>
          {isSoldOut ? "Čakacia listina je otvorená" : `${seats} voľných miest`}
        </span>
      ) : null}
      <span className="vg-bratislava-event-card__arrow" aria-hidden="true">→</span>
    </motion.button>
  );
};

export default function BratislavaEventsLanding({ isGuest = true }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add("vg-bratislava-page-open");
    document.body.classList.add("vg-bratislava-page-open");
    return () => {
      document.documentElement.classList.remove("vg-bratislava-page-open");
      document.body.classList.remove("vg-bratislava-page-open");
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    api
      .get("/events/events_packs/", {
        params: {
          city: "Bratislava",
          country: "Slovakia",
          type: "all",
          page: 1,
          page_size: EVENT_LIMIT + 4,
        },
        signal: controller.signal,
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      })
      .then(({ data }) => {
        if (!isMounted) return;
        const nextEvents = Array.isArray(data?.results)
          ? data.results.filter(isPublicBratislavaEvent).slice(0, EVENT_LIMIT)
          : [];
        setEvents(nextEvents);
      })
      .catch((error) => {
        if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
          console.error("[BratislavaEventsLanding] Failed to load events", error);
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const visibleEvents = useMemo(() => events.slice(0, EVENT_LIMIT), [events]);
  const openCatalog = () => navigate("/events/search");
  const openRegistration = () => navigate(isGuest ? "/auth?tab=register" : "/events/search");
  const openEvent = (event) => navigate(buildEventPath(event.id, event.name));

  return (
    <div className="vg-bratislava-landing">
      <Helmet>
        <html lang="sk" />
        <title>Podujatia v Bratislave | Aktívny život s VaultGrid</title>
        <meta
          name="description"
          content="Pridajte sa k lokálnym podujatiam v Bratislave alebo vytvorte vlastné. Šport, voľný čas, turistika a vzdelávanie s hostiteľom, ktorý drží aktivitu pokope."
        />
        <meta name="robots" content="index,follow,noimageindex" />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="VaultGrid" />
        <meta property="og:title" content="Podujatia v Bratislave | VaultGrid" />
        <meta
          property="og:description"
          content="Lokálne podujatia vytvorené ľuďmi pre ľudí — s hostiteľom, spoločnou aktivitou a priestorom pre nové kontakty."
        />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content="https://vaultgrid.net/android-chrome-512x512.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <header>
        <LandingHeaderHeroFrame>
          <button type="button" className={landingHeaderStyles.logoWrapper} onClick={() => navigate("/")}>
            <img className={landingHeaderStyles.logo} src="/assets/landing/castle-logo.svg" alt="VaultGrid" />
            <span className={landingHeaderStyles.brandText}>
              vaultgrid<span className={landingHeaderStyles.betaText}>Beta</span>
            </span>
          </button>
          <div className="vg-bratislava-landing__header-actions">
            <button type="button" className="vg-bratislava-landing__header-link" onClick={openCatalog}>
              Všetky podujatia
            </button>
            <button type="button" className="vg-bratislava-landing__header-cta" onClick={openRegistration}>
              Pridať sa
            </button>
          </div>
        </LandingHeaderHeroFrame>
      </header>

      <main>
        <section className="vg-bratislava-hero">
          <div className="vg-bratislava-hero__orb vg-bratislava-hero__orb--one" />
          <div className="vg-bratislava-hero__orb vg-bratislava-hero__orb--two" />
          <div className="vg-bratislava-hero__content">
            <p className="vg-bratislava-eyebrow">AKTÍVNY ŽIVOT V BRATISLAVE</p>
            <h1>Lokálne podujatia pre aktívny život v Bratislave.</h1>
            <p className="vg-bratislava-hero__lead">
              Pridajte sa k lokálnym podujatiam vytvoreným ľuďmi pre ľudí — alebo vytvorte vlastné.
              Šport, voľný čas, turistika a vzdelávanie: vyberte si formát, ktorý vám sedí.
            </p>
            <div className="vg-bratislava-hero__actions">
              <motion.button
                type="button"
                className="vg-bratislava-button vg-bratislava-button--primary"
                onClick={openCatalog}
                whileHover={PRESS_HOVER}
                whileTap={PRESS_TAP}
                transition={PRESS_TRANSITION}
              >
                Pozrieť podujatia <span aria-hidden="true">→</span>
              </motion.button>
              <button type="button" className="vg-bratislava-button vg-bratislava-button--quiet" onClick={() => navigate("/about?tab=about")}>
                Ako funguje VaultGrid
              </button>
            </div>
          </div>
          <aside className="vg-bratislava-hero__promise" aria-label="Prečo VaultGrid">
            <span className="vg-bratislava-hero__promise-kicker">PRÍĎTE AJ SAMI</span>
            <strong>Každé podujatie má<br />svojho hostiteľa.</strong>
            <p>Hostiteľ vás privíta, vedie aktivitu a drží jej priebeh pokope. Vy sa prirodzene zapojíte do spoločného zážitku.</p>
          </aside>
        </section>

        <section className="vg-bratislava-events" aria-labelledby="bratislava-events-heading">
          <div className="vg-bratislava-section-heading">
            <div>
              <p className="vg-bratislava-eyebrow">ČO JE TERAZ V MESTE</p>
              <h2 id="bratislava-events-heading">Najbližšie podujatia v Bratislave</h2>
            </div>
            <button type="button" onClick={openCatalog}>Zobraziť všetky <span aria-hidden="true">→</span></button>
          </div>

          {isLoading ? (
            <div className="vg-bratislava-event-grid" aria-label="Načítavanie podujatí">
              {[0, 1, 2].map((item) => <div key={item} className="vg-bratislava-event-card vg-bratislava-event-card--skeleton" />)}
            </div>
          ) : visibleEvents.length ? (
            <div className="vg-bratislava-event-grid">
              {visibleEvents.map((event) => <EventCard key={event.id} event={event} onOpen={openEvent} />)}
            </div>
          ) : (
            <div className="vg-bratislava-empty-state">
              <p>Nové podujatia sa objavujú priebežne. Otvorte si aktuálny prehľad vo VaultGrid.</p>
              <button type="button" onClick={openCatalog}>Otvoriť prehľad podujatí</button>
            </div>
          )}
        </section>

        <section className="vg-bratislava-why">
          <div>
            <p className="vg-bratislava-eyebrow">AKO FUNGUJE VAULTGRID</p>
            <h2>Od podujatia k ľuďom, s ktorými si sadnete.</h2>
          </div>
          <div className="vg-bratislava-why__points">
            <article><span>01</span><h3>Vyberiete si podujatie a ľudí okolo neho</h3><p>V prehľade vidíte termín, miesto, kapacitu, účastníkov aj hostiteľa, ktorému môžete ešte predtým napísať otázku.</p></article>
            <article><span>02</span><h3>Po pridaní ste v chate podujatia</h3><p>S ostatnými účastníkmi sa môžete spojiť ešte pred stretnutím, doladiť detaily alebo sa opýtať hostiteľa.</p></article>
            <article><span>03</span><h3>Prídete, potvrdíte účasť a zapojíte sa</h3><p>Krátky check-in cez hostiteľa alebo polohu potvrdí vašu účasť a buduje hodnotenie vašej spoľahlivosti. Hostiteľ vedie aktivitu tak, aby sa každý prirodzene zapojil.</p></article>
            <article><span>04</span><h3>Po podujatí učíte odporúčania pre seba</h3><p>Označíte ľudí, s ktorými vám bolo dobre. VaultGrid tak lepšie odporúča ďalšie podujatia; prípadné porušenia pravidiel môžete nahlásiť, aby komunita zostala bezpečná.</p></article>
          </div>
        </section>

        <section className="vg-bratislava-final-cta">
          <p className="vg-bratislava-eyebrow">VIAC AKTIVITY, VIAC SPOLOČNÝCH ZÁŽITKOV</p>
          <h2>Nájdite svoju komunitu v Bratislave.</h2>
          <button type="button" className="vg-bratislava-button vg-bratislava-button--primary" onClick={openRegistration}>
            Vstúpiť do VaultGrid <span aria-hidden="true">→</span>
          </button>
        </section>
      </main>
    </div>
  );
}
